import React, { useEffect, useState } from 'react';
import { auth, db } from './firebase'; 
import { collection, getDocs, addDoc, serverTimestamp, query, where, doc, getDoc } from 'firebase/firestore'; 
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from "firebase/auth"; 
import AppHeader from "./AppHeader";

const BusinessList = () => {
  const [businesses, setBusinesses] = useState([]);
  const [followedBusinesses, setFollowedBusinesses] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  const styles = {
    pageWrapper: { backgroundColor: '#f8fafc', minHeight: '100vh' },
    container: { padding: '40px 20px', fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif' },
    header: { textAlign: 'center', marginBottom: '40px' },
    mainTitle: { fontSize: '32px', fontWeight: '800', color: '#1a202c', letterSpacing: '-0.5px', margin: '0' },
    subTitle: { color: '#64748b', fontSize: '16px', marginTop: '8px' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '30px', maxWidth: '1200px', margin: '0 auto' },
    card: { backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)', position: 'relative', transition: 'all 0.3s ease', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', flexDirection: 'column' },
    coverImg: { width: '100%', height: '160px', objectFit: 'cover', backgroundColor: '#edf2f7' },
    logoContainer: { position: 'absolute', top: '120px', left: '24px', backgroundColor: '#fff', borderRadius: '10px', padding: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' },
    logo: { width: '70px', height: '70px', borderRadius: '8px', objectFit: 'contain' },
    content: { padding: '45px 24px 24px 24px', flexGrow: 1 },
    category: { fontSize: '12px', color: '#005b63', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', display: 'block' },
    title: { fontSize: '20px', fontWeight: '700', margin: '0 0 10px 0', color: '#0f172a', lineHeight: '1.2' },
    desc: { fontSize: '14px', color: '#475569', lineHeight: '1.6', height: '45px', overflow: 'hidden', marginBottom: '20px' },
    followBtn: { width: '100%', padding: '10px', backgroundColor: '#005b63', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'background 0.2s ease' },
    followingBtn: { width: '100%', padding: '10px', backgroundColor: '#e2e8f0', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'default' }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login");
      } else {
        setCurrentUser(user);
        fetchBusinesses(user.uid);
      }
    });

    const fetchBusinesses = async (uid) => {
      try {
        const querySnapshot = await getDocs(collection(db, "businessPages"));
        const bizList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBusinesses(bizList);

        const followQuery = query(collection(db, "businessFollowers"), where("followerUid", "==", uid));
        const followSnapshot = await getDocs(followQuery);
        const follows = {};
        followSnapshot.forEach(doc => {
          follows[doc.data().businessId] = true;
        });
        setFollowedBusinesses(follows);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    return () => unsubscribe();
  }, [navigate]);

  const handleCardClick = (biz) => {
    const slug = biz.businessName.toLowerCase().replace(/[\s-]/g, "");
    navigate(`/business-view/${slug}`, { 
      state: { businessId: biz.id, businessName: biz.businessName } 
    });
  };

  const handleFollow = async (e, biz) => {
    e.stopPropagation(); 
    if (!currentUser) return;

    try {
      const profileQuery = query(collection(db, "profiles"), where("email", "==", currentUser.email));
      const profileSnapshot = await getDocs(profileQuery);
      
      let followerDetails = {
        followerUid: currentUser.uid,
        followerEmail: currentUser.email,
        businessId: biz.id,
        businessName: biz.businessName, 
        followedAt: serverTimestamp()
      };

      if (!profileSnapshot.empty) {
        const pDoc = profileSnapshot.docs[0];
        const pData = pDoc.data();
        
        followerDetails = {
          ...followerDetails,
          followerName: pData.fullName || "User",
          followerImage: pData.profileImageUrl || "",
          // Profiles collection-il irundhu cover image-ai edukkirōm
          followerCover: pData.coverImageUrl || "", 
          mainRole: pData.mainRole || "Public User",
          followerBusinessName: pData.mainRole === "Business Owner" ? pData.businessName : ""
        };
      }

      await addDoc(collection(db, "businessFollowers"), followerDetails);
      setFollowedBusinesses(prev => ({ ...prev, [biz.id]: true }));
      alert(`You started following ${biz.businessName}`);
    } catch (error) {
      console.error("Error following business:", error);
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <AppHeader />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.mainTitle}>Explore Businesses</h1>
        </div>
        <div style={styles.grid}>
          {businesses.map((biz) => {
            const isFollowing = followedBusinesses[biz.id];
            return (
              <div key={biz.id} style={styles.card} onClick={() => handleCardClick(biz)}>
                <img src={biz.coverImageUrl || 'https://via.placeholder.com/400x160'} alt="Cover" style={styles.coverImg} />
                <div style={styles.logoContainer}>
                  <img src={biz.logoUrl || 'https://via.placeholder.com/70'} alt="Logo" style={styles.logo} />
                </div>
                <div style={styles.content}>
                  
                  <h2 style={styles.title}>{biz.businessName}</h2>
                  <p style={styles.desc}>{biz.description}</p>
                  {isFollowing ? (
                    <button style={styles.followingBtn}>Following</button>
                  ) : (
                    <button style={styles.followBtn} onClick={(e) => handleFollow(e, biz)}>Follow</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BusinessList;